import { FC, memo, ReactNode } from 'react';
import {
  BarChartOutlined,
  FileDoneOutlined,
  PhoneOutlined,
  SettingOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { selectRoles, selectUser } from '../../store/reducers/AuthSlice';
import { useAppSelector } from '../../store/store';
import { getRoleLabels } from '../../models/enums/roleLabels';
import { HomeReleaseFooter } from '../../components/HomeReleaseFooter/HomeReleaseFooter';
import styles from './style.module.css';

interface HomeSection {
  readonly title: string;
  readonly description: string;
  readonly icon: ReactNode;
  readonly tone: 'blue' | 'violet' | 'green' | 'orange' | 'red' | 'cyan';
}

const homeSections: readonly HomeSection[] = [
  {
    title: 'Журналы заданий',
    description:
      'Просматривайте результаты взаимодействия с абонентами, детали звонков и записи разговоров. Загружайте задания и формируйте отчеты по работе системы',
    icon: <FileDoneOutlined />,
    tone: 'blue',
  },
  {
    title: 'Статистика и аналитика',
    description:
      'Анализируйте результаты взаимодействия с абонентами, отслеживайте показатели работы системы и просматривайте детализацию по звонкам и недозвонам. Формируйте отчеты по выбранным периодам',
    icon: <BarChartOutlined />,
    tone: 'violet',
  },
  {
    title: 'Параметры работы системы',
    description:
      'Настраивайте параметры обработки звонков: графики обзвона, количество попыток дозвона, интервалы повторных вызовов и сценарии взаимодействия',
    icon: <SettingOutlined />,
    tone: 'green',
  },
  {
    title: 'Управление пользователями',
    description:
      'Создавайте и редактируйте учетные записи пользователей, назначайте роли, управляйте активностью сотрудников и списком доступных медицинских организаций',
    icon: <TeamOutlined />,
    tone: 'cyan',
  },
  {
    title: 'Чёрный список',
    description:
      'Управляйте списком номеров, для которых запрещено совершение исходящих вызовов. Добавляйте номера в список блокировки и при необходимости удаляйте их из списка',
    icon: <StopOutlined />,
    tone: 'red',
  },
  {
    title: 'Профиль',
    description:
      'Просматривайте данные своей учетной записи, включая логин и назначенную роль пользователя',
    icon: <UserOutlined />,
    tone: 'orange',
  },
];

const HomeIllustration: FC = () => (
  <div className={styles.illustration} aria-hidden="true">
    <span className={styles.dotBlue} />
    <span className={styles.dotViolet} />
    <span className={styles.dotGreen} />
    <span className={styles.dotSky} />
    <span className={styles.lineOne} />
    <span className={styles.lineTwo} />
    <span className={styles.lineThree} />
    <span className={styles.menuLines}>
      <span />
      <span />
      <span />
    </span>
    <span className={`${styles.visualTile} ${styles.visualTilePhone}`}>
      <PhoneOutlined />
    </span>
    <span className={`${styles.visualTile} ${styles.visualTileUser}`}>
      <UserOutlined />
    </span>
    <span className={`${styles.visualTile} ${styles.visualTileChart}`}>
      <BarChartOutlined />
    </span>
  </div>
);

const HomeScreenComponent: FC = () => {
  const userName = useAppSelector(selectUser);
  const roles = useAppSelector(selectRoles);
  const roleLabel = getRoleLabels(roles ?? []);
  const displayedUserName = userName || 'Пользователь';
  const displayedRole = roleLabel || 'Роль не назначена';

  return (
    <main className={styles.page}>
      <div className={styles.inner}>
        <header className={styles.topBar} aria-label="Данные пользователя">
          <div className={styles.userBadge}>
            <span className={styles.userIcon} aria-hidden="true">
              <UserOutlined />
            </span>
            <span className={styles.userText}>
              <strong>{displayedUserName}</strong>
              <span>{displayedRole}</span>
            </span>
          </div>
        </header>

        <section className={styles.hero} aria-labelledby="home-title">
          <div className={styles.heroCopy}>
            <h1 id="home-title">
              Веб-интерфейс Системы MedVox, осуществляющей взаимодействие с
              Абонентом с помощью голоса
            </h1>
            <p>
              Настраивайте параметры работы системы, управляйте пользователями и
              телефонными линиями, просматривайте журналы звонков, записи
              разговоров и статистику взаимодействия с абонентами
            </p>
          </div>
          <HomeIllustration />
        </section>

        <section
          className={styles.sectionsGrid}
          aria-label="Основные разделы системы"
        >
          {homeSections.map((section) => (
            <article key={section.title} className={styles.sectionCard}>
              <span
                className={`${styles.sectionIcon} ${styles[section.tone]}`}
                aria-hidden="true"
              >
                {section.icon}
              </span>
              <div className={styles.sectionText}>
                <h2>{section.title}</h2>
                <p>{section.description}</p>
              </div>
            </article>
          ))}
        </section>

        <HomeReleaseFooter />
      </div>
    </main>
  );
};

export const HomeScreen = memo(HomeScreenComponent);
HomeScreen.displayName = 'HomeScreen';
